import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Table } from 'antd'
import { Container } from 'typescript-ioc'
import { emptyStates } from '@pushly/aqe/lib/components'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { DataSourceResponse } from '@pushly/aqe/lib/components/data-view/types'
import { CatalogService } from '../../services/catalog.service'
import { CatalogItemResponse, CatalogItemType } from './types'
import { getColumnsForItemType } from './columns'
import { LocaleSelector } from './locale-selector'
import { FeatureEmptyState } from '../../components/icons/feature-icon'

export interface CatalogDataViewProps {
    domainId: number
    itemType: CatalogItemType
    locale?: string
    onLocaleChange?: (locale: string) => void
    catalogLocales?: string[]
}

/**
 * CatalogDataView component for displaying catalog items in a paginated, searchable table.
 * Uses the DataView pattern from @pushly/aqe for state management and data fetching.
 */
export const CatalogDataView: React.FC<CatalogDataViewProps> = ({
    domainId,
    itemType,
    locale,
    onLocaleChange,
    catalogLocales,
}) => {
    const catalogService = Container.get(CatalogService)
    const [hasError, setHasError] = useState(false)

    // Use a ref so the dataSourceLoader closure always reads the current locale
    // without needing to recreate the callback (which the DataView wouldn't detect anyway).
    const localeRef = useRef(locale)
    localeRef.current = locale

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<CatalogItemResponse>({
        id: `catalog-${itemType}`,
        levelResource: {
            level: 'domain',
            id: domainId,
        },
        defaultFilters: {},
    })

    const itemTypeLabel = itemType.charAt(0).toUpperCase() + itemType.slice(1)
    const columns = getColumnsForItemType(itemType)

    /**
     * Data source loader for fetching catalog items.
     * Handles search filtering and pagination.
     */
    const dataSourceLoader = useCallback(
        async (
            filters: Record<string, any>,
            tableState: { pagination: { current?: number; pageSize?: number } },
        ): Promise<DataSourceResponse<CatalogItemResponse>> => {
            const query: Record<string, any> = {}

            if (localeRef.current) {
                query.locale = localeRef.current
            }

            if (filters.search) {
                query.search = filters.search
            }

            if (tableState.pagination.current) {
                query.page = tableState.pagination.current
            }

            if (tableState.pagination.pageSize) {
                query.limit = tableState.pagination.pageSize
            }

            const response = await catalogService.fetchCatalogItems(domainId, {
                query,
                cancellationKey: 'catalog-data-view.fetch',
            })

            if (response.cancelled) {
                return {
                    ok: false,
                    data: [],
                    cancelled: true,
                }
            }

            if (!response.ok) {
                setHasError(true)
                return {
                    ok: false,
                    data: [],
                }
            }

            setHasError(false)
            return {
                ok: response.ok,
                data: response.data ?? [],
                meta: response.meta,
            }
        },
        [domainId, catalogService],
    )

    // Trigger a data reload when locale changes
    const prevLocaleRef = useRef(locale)
    useEffect(() => {
        if (prevLocaleRef.current !== locale && locale !== undefined) {
            prevLocaleRef.current = locale
            requestDataSourceReload()
        }
    }, [locale, requestDataSourceReload])

    const handleRetry = () => {
        setHasError(false)
        requestDataSourceReload()
    }

    const showLocaleSelector = !!catalogLocales && catalogLocales.length > 1 && !!locale && !!onLocaleChange

    return (
        <DataViewContext.Provider value={{ state: context, dispatch }}>
            <DataView<CatalogItemResponse>
                dataSourceLoader={dataSourceLoader}
                hideAutoRefreshOptions={true}
                filtersAddonAfter={
                    showLocaleSelector ? (
                        <LocaleSelector
                            locales={catalogLocales}
                            selectedLocale={locale}
                            onLocaleChange={onLocaleChange}
                        />
                    ) : undefined
                }
                filters={[
                    {
                        key: 'search',
                        type: 'search',
                        componentProps: {
                            className: 'catalog-list-search',
                            placeholder: `Search by name, URL, or other ${itemType} property.`,
                            style: { width: 400 },
                            // The no-results copy tells the user to clear the search, so the control has
                            // to live on the input: the empty state's Clear-filters button can reset
                            // the filter value but not the term the filter holds in local state.
                            allowClear: true,
                        },
                    },
                ]}
                table={{
                    rowKey: (row) => row.item_id,
                    // Table chrome follows the load, not the row count. A load that succeeded still
                    // has real columns and real pagination links even when it returned zero rows, so
                    // the header row and the pager hold through both empty states; a failed load has
                    // neither, so both drop while the failure notice fills the table body.
                    showHeader: !hasError,
                    hideFooter: hasError,
                    noData: hasError
                        ? emptyStates.error({
                              text: 'Unable to load your catalog',
                              description: 'Please try again. If the problem persists, contact your account manager.',
                              onRetry: handleRetry,
                          })
                        : (ctx) =>
                              ctx.hasActiveFilters ? (
                                  emptyStates.noResults({
                                      text: `No ${itemTypeLabel}s match your search`,
                                      description: 'Clear search to see all items.',
                                  })
                              ) : (
                                  <FeatureEmptyState
                                      slot="nav:catalogs"
                                      text="Your catalog is empty"
                                      description="Contact your account manager for more information."
                                  />
                              ),
                }}
            >
                {columns.map((column) => (
                    <Table.Column<CatalogItemResponse>
                        key={column.key}
                        dataIndex={column.dataIndex}
                        title={column.title}
                        width={column.width}
                        ellipsis={column.ellipsis}
                        render={column.render}
                    />
                ))}
            </DataView>
        </DataViewContext.Provider>
    )
}
